/**
 * AP-PORTAL-01: Supplier Portal Account Activation
 * Route: /portal/activate?token=<token>
 *
 * Supplier sets their password on first login after receiving an invitation.
 * Token is consumed server-side after successful activation.
 */
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, AlertCircle, Eye, EyeOff } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function SupplierPortalActivate() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) setError("Invalid or missing activation link. Please request a new invitation.");
  }, [token]);

  async function handleActivate(e) {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API}/api/portal/auth/activate`,
        { token, password, name: name.trim() || undefined },
        { withCredentials: true }
      );
      setSuccess(true);
      setTimeout(() => navigate("/portal/dashboard", { replace: true }), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || "Activation failed. The link may be expired.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      data-testid="portal-activate-page"
      style={{
        minHeight: "100vh",
        background: "#111010",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      <div style={{ marginBottom: "40px", textAlign: "center" }}>
        <p style={{ fontSize: "11px", letterSpacing: "4px", textTransform: "uppercase", color: "#C9A15F", marginBottom: "8px" }}>
          Prashasta Events
        </p>
        <h1
          style={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontSize: "clamp(26px, 5vw, 34px)",
            fontWeight: 600,
            color: "#F5EFE6",
            margin: 0,
          }}
        >
          Activate Your Account
        </h1>
        <p style={{ fontSize: "13px", color: "#8A7A6A", marginTop: "6px" }}>
          Set your password to access the Partner Portal
        </p>
      </div>

      <div
        style={{
          width: "100%",
          maxWidth: "420px",
          background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(201,161,95,0.2)",
          borderRadius: "16px",
          padding: "clamp(24px, 5vw, 40px)",
          backdropFilter: "blur(12px)",
        }}
      >
        {success ? (
          <div data-testid="portal-activate-success" style={{ textAlign: "center", padding: "16px 0" }}>
            <CheckCircle2 size={40} style={{ color: "#7EC98A", margin: "0 auto 16px" }} />
            <p style={{ color: "#F5EFE6", fontWeight: 600, marginBottom: "8px" }}>Account activated!</p>
            <p style={{ color: "#8A7A6A", fontSize: "13px" }}>Redirecting to your dashboard…</p>
          </div>
        ) : (
          <form onSubmit={handleActivate} data-testid="portal-activate-form">
            {/* Name */}
            <div style={{ marginBottom: "18px" }}>
              <label style={labelStyle}>Your name (optional)</label>
              <input
                data-testid="portal-activate-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Rahul Sharma"
                style={inputStyle}
                onFocus={(e) => (e.target.style.borderColor = "rgba(201,161,95,0.6)")}
                onBlur={(e) => (e.target.style.borderColor = "rgba(255,255,255,0.12)")}
              />
            </div>

            {/* Password */}
            <div style={{ marginBottom: "18px" }}>
              <label style={labelStyle}>New password</label>
              <div style={{ position: "relative" }}>
                <input
                  data-testid="portal-activate-password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimum 8 characters"
                  style={{ ...inputStyle, paddingRight: "44px" }}
                  onFocus={(e) => (e.target.style.borderColor = "rgba(201,161,95,0.6)")}
                  onBlur={(e) => (e.target.style.borderColor = "rgba(255,255,255,0.12)")}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#6B5B4A" }}
                >
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            {/* Confirm */}
            <div style={{ marginBottom: "24px" }}>
              <label style={labelStyle}>Confirm password</label>
              <input
                data-testid="portal-activate-confirm"
                type="password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repeat your password"
                style={inputStyle}
                onFocus={(e) => (e.target.style.borderColor = "rgba(201,161,95,0.6)")}
                onBlur={(e) => (e.target.style.borderColor = "rgba(255,255,255,0.12)")}
              />
            </div>

            {error && (
              <div
                data-testid="portal-activate-error"
                style={{ display: "flex", alignItems: "flex-start", gap: "8px", padding: "12px", background: "rgba(220,50,50,0.1)", border: "1px solid rgba(220,50,50,0.3)", borderRadius: "8px", marginBottom: "16px" }}
              >
                <AlertCircle size={14} style={{ color: "#E57373", flexShrink: 0, marginTop: "1px" }} />
                <span style={{ fontSize: "13px", color: "#E57373" }}>{error}</span>
              </div>
            )}

            <button
              type="submit"
              data-testid="portal-activate-submit"
              disabled={loading || !token}
              style={{
                width: "100%",
                padding: "13px",
                background: loading || !token ? "rgba(201,161,95,0.4)" : "#C9A15F",
                color: "#111010",
                border: "none",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: 600,
                cursor: loading || !token ? "not-allowed" : "pointer",
                letterSpacing: "0.3px",
              }}
            >
              {loading ? "Activating…" : "Activate Account"}
            </button>

            <p style={{ textAlign: "center", marginTop: "16px", fontSize: "12px", color: "#4A3A2A" }}>
              Already activated?{" "}
              <Link to="/portal/login" style={{ color: "#C9A15F", textDecoration: "none" }}>
                Sign in
              </Link>
            </p>
          </form>
        )}
      </div>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        input::placeholder { color: #4A3A2A; }
      `}</style>
    </div>
  );
}

const labelStyle = {
  display: "block",
  fontSize: "12px",
  color: "#A89880",
  marginBottom: "6px",
  letterSpacing: "0.5px",
};

const inputStyle = {
  width: "100%",
  padding: "12px 16px",
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: "8px",
  color: "#F5EFE6",
  fontSize: "14px",
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color 0.2s",
};
